import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { useOrderHistory } from '../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../stores/appCostPriceCategoriesStore';

/** Generation can add supplier connections; rebuild the history scope after those are loaded. */
export async function refreshDemoHistory(restaurantId: string): Promise<void> {
  await useRestaurantStore.getState().loadAllData(true);
  // The request can finish after the user has left this restaurant.
  if (useAppUserDefaultStore.getState().restaurant?.id !== restaurantId) return;

  const restaurants = useRestaurantStore.getState();
  const platformIds = (restaurants.restaurantSections[restaurantId] ?? []).flatMap(section =>
    (restaurants.restaurantSectionPlatforms[section.id] ?? []).map(platform => platform.id)
  );
  const revenueSourceIds = (restaurants.restaurantRevenueSources[restaurantId] ?? []).map(source => source.id);
  const orders = useOrderHistory.getState();
  const revenue = useRevenueHistory.getState();
  // Cancel pre-generation loads and bypass caches even when no connections were added.
  orders.clearOrderhistoryDataForRestaurant(restaurantId);
  revenue.clearRevenuehistoryDataForRestaurant(restaurantId);
  await Promise.all([
    orders.loadByPlatformIds(restaurantId, platformIds, restaurants.platformToSectionIndex),
    revenue.loadByRevenueSourceIds(restaurantId, revenueSourceIds, restaurants.restaurantRevenueSources),
    // New sales need their saved recipes and categories, even if these stores were already loaded.
    useCostPriceStore.getState().load(restaurantId, {
      force: true,
      isCurrent: () => useAppUserDefaultStore.getState().restaurant?.id === restaurantId,
    }),
    useCostPriceCategoriesStore.getState().load(restaurantId, { force: true }),
  ]);
  if (useAppUserDefaultStore.getState().restaurant?.id !== restaurantId) return;
  // Stores log fetch errors internally; don't present an incomplete refresh as success.
  if (!useOrderHistory.getState().isDataLoadedForPlatforms(platformIds)
      || !useRevenueHistory.getState().isDataLoadedForRevenueSources(revenueSourceIds)
      || !useCostPriceStore.getState().isDataLoadedFor(restaurantId)
      || useCostPriceStore.getState().error
      || !useCostPriceCategoriesStore.getState().isDataLoadedFor(restaurantId)
      || useCostPriceCategoriesStore.getState().error) {
    throw new Error('Demo history refresh incomplete');
  }
}
